/**
 * Cognito's `ChangePassword` from the browser (SPEC decision 38): the session's access token authorizes it, Cognito
 * checks the current password, and nothing passes through Hangar's API. CORS is open on Cognito's endpoint (and on the
 * emulator's).
 */
import { config } from '../config';
import { cognitoApiOf, passwordChangeFailure, type PasswordChangeFailure } from './passwordModel';
import { currentUser } from './userManager';

/** True when the console signs in through a Cognito user pool, the only issuer with passwords to change. */
export const canChangePassword = (): boolean => Boolean(cognitoApiOf(config.authority));

/** Resolves when Cognito accepted the new password; rejects with a `PasswordChangeFailure` otherwise. */
export async function changePassword(previous: string, proposed: string): Promise<void> {
  const endpoint = cognitoApiOf(config.authority);
  const accessToken = (await currentUser())?.access_token;
  if (!endpoint || !accessToken) throw { message: 'Sign in again to change your password.' } satisfies PasswordChangeFailure;
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/x-amz-json-1.1', 'x-amz-target': 'AWSCognitoIdentityProviderService.ChangePassword' },
      body: JSON.stringify({ AccessToken: accessToken, PreviousPassword: previous, ProposedPassword: proposed }),
    });
  } catch {
    throw { message: 'Could not reach the sign-in service. Check your connection and try again.' } satisfies PasswordChangeFailure;
  }
  if (res.ok) return;
  const body = (await res.json().catch(() => ({}))) as { __type?: string; message?: string };
  throw passwordChangeFailure(body.__type, body.message);
}
